import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import { Tab, Tabs, Tooltip, useTheme, Button } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { PageLayout } from '../../PageLayout';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { TabPanel } from '../../Atoms/TabPanel';
import {
  ApiAdvancedFilter,
  getSelectedTab,
  PaginatedResponse,
  updateState,
  FilterableField
} from '../../../store/GeneralStore';
import { Payment, fetchPayments, getTallyPaymentsSubject } from '../../../store/Payment';
import { DefaultPaymentGridColumnDefs } from './PaymentGridColumnDefs';
import {
  getCustomizationSubject,
  getCustomViewSubject,
  CustomView,
  PrefKey,
  UserCustomizations
} from '../../../store/UserCustomizations';
import {
  buildAdvancedFilterSet,
  getColsForTab,
  getInitialState,
  mergeAndDeduplicateFilters
} from '../../Compounds/UpstackDataGrid/helpers';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { DEFAULT_TAB_SX_STYLES } from '../../../utils/styleHelpers';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { Menu as CustomViewMenu } from '../../Compounds/CustomTableViews/Menu';
import { PaymentRecipientType } from '../../../store/PaymentRecipient';
import { NewPaymentDialog } from './NewPaymentDialog';

export interface TabProps {
  name: string;
  customView?: boolean;
  filter?: FilterableField[];
  tooltip?: string;
  disabledFilters?: string[];
}

export const PaymentPage = () => {
  const [paymentsLoading, setPaymentsLoading] = useState<boolean>(false);
  const [payments, setPayments] = useState<PaginatedResponse<Payment>>();
  const [userCustomizations, setUserCustomizations] = useState<UserCustomizations>();
  const [selectedTab, setSelectedTab] = useState(getSelectedTab().payments);
  const [customViews, setCustomViews] = useState<CustomView[]>([]);
  const [customViewNames, setCustomViewNames] = useState<string[]>([]);
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [newlyCreatedViewName, setNewlyCreatedViewName] = useState<string | null>(null);
  const [newPaymentOpen, setNewPaymentOpen] = useState<boolean>(false);

  const theme = useTheme();
  const filterKeys = DefaultPaymentGridColumnDefs()
    .filter((column) => column.filterable)
    .map((column) => column.field);

  const defaultTabs = useMemo(
    () =>
      [
        ...Object.values(PaymentRecipientType).map((type) => ({
          name: type,
          filter: [
            {
              operator: 'equals',
              field: 'recipientType',
              value: type
            }
          ],
          tooltip: `Payment Recipient Type equals ${type}`,
          disabledFilters: ['recipientType']
        })),
        {
          name: 'All',
          filter: [],
          tooltip: 'Unfiltered',
          disabledFilters: []
        }
      ] as TabProps[],
    []
  );

  const allAvailableViews = useMemo(() => {
    const customViewsTabProps: TabProps[] = customViews
      .filter((view) => view.page === 'paymentGrid_')
      .map((t) => ({ name: t.name, customView: true }));

    return [...defaultTabs, ...customViewsTabProps];
  }, [defaultTabs, customViews]);

  const handleFetchPayments = useCallback(
    async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
      if (localLoading) setPaymentsLoading(true);

      let currentTabFilters: FilterableField[] = [];
      const currentSelectedView = allAvailableViews[selectedTab];

      if (currentSelectedView) {
        currentTabFilters = (currentSelectedView.filter as FilterableField[]) || [];
      }

      await fetchPayments(
        {
          ...filters,
          filters: mergeAndDeduplicateFilters(filters?.filters || [], currentTabFilters, true)
        },
        forceRefresh
      );
      if (localLoading) setPaymentsLoading(false);
    },
    [allAvailableViews, selectedTab]
  );

  const handleTabChange = useCallback(
    (index: number, name: string, type: 'main' | 'custom') => {
      if (type === 'main') {
        setSelectedCustomView('');
      } else if (type === 'custom') {
        setSelectedCustomView(name);
      }

      const actualIndex = allAvailableViews.findIndex((tab) => {
        if (type === 'custom') {
          return tab.name === name && tab.customView;
        } else {
          return tab.name === name && !tab.customView;
        }
      });

      if (actualIndex !== -1) {
        updateState({
          selectedTab: {
            ...getSelectedTab(),
            payments: actualIndex
          }
        });
        setSelectedTab(actualIndex);
      }
    },
    [allAvailableViews]
  );

  const getInitialSort = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `paymentGrid_${pageName}` as PrefKey;
      const savedSort = userCust?.content?.[currentPage]?.muiConfig?.sorting?.sortModel;
      return savedSort?.length ? savedSort : ([] as GridSortModel);
    },
    [allAvailableViews, selectedTab]
  );

  const getInitialFilter = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `paymentGrid_${pageName}` as PrefKey;
      const savedFilter = userCust?.content?.[currentPage]?.muiConfig?.filter?.filterModel;
      return savedFilter || ({} as GridFilterModel);
    },
    [allAvailableViews, selectedTab]
  );

  const dataFetch = useCallback(() => {
    if (userCustomizations && allAvailableViews[selectedTab]) {
      const initialSortModel = getInitialSort(userCustomizations);
      const initialFilterModel = getInitialFilter(userCustomizations);
      handleFetchPayments(
        {
          ...buildAdvancedFilterSet(initialFilterModel || ({} as GridFilterModel), filterKeys),
          orders: initialSortModel
            ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
            : undefined
        },
        true,
        true
      );
    }
  }, [selectedTab, userCustomizations, allAvailableViews, getInitialSort, getInitialFilter, filterKeys]);

  const handleNewViewCreated = useCallback((viewName: string) => {
    setNewlyCreatedViewName(viewName);
  }, []);

  useEffect(() => {
    const assetSub = getTallyPaymentsSubject().subscribe((a) => setPayments(a));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => setUserCustomizations(userCust));
    const customViewsSub = getCustomViewSubject().subscribe((custViews) => setCustomViews(custViews));

    return () => {
      if (assetSub) assetSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
      if (customViewsSub) customViewsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setCustomViewNames(customViews.filter((view) => view.page === 'paymentGrid_').map((v) => v.name));
  }, [customViews]);

  useEffect(() => {
    if (allAvailableViews.length > 0 && selectedTab !== undefined && selectedTab >= 0) {
      const initialSelectedView = allAvailableViews[selectedTab];
      if (initialSelectedView) {
        if (initialSelectedView.customView) {
          setSelectedCustomView(initialSelectedView.name);
        } else {
          setSelectedCustomView('');
        }
      }
    }
  }, [allAvailableViews, selectedTab]);

  useEffect(() => {
    if (newlyCreatedViewName) {
      const newViewIndex = allAvailableViews.findIndex((tab) => tab.name === newlyCreatedViewName && tab.customView);
      if (newViewIndex !== -1) {
        handleTabChange(newViewIndex, newlyCreatedViewName, 'custom');
        setNewlyCreatedViewName(null);
      }
    }
  }, [newlyCreatedViewName, allAvailableViews, handleTabChange]);

  useEffect(() => {
    if (userCustomizations && allAvailableViews.length > 0) {
      dataFetch();
    }
  }, [selectedTab, userCustomizations, allAvailableViews]);

  useEffect(() => {
    if (selectedCustomView && !customViewNames.includes(selectedCustomView)) {
      setSelectedCustomView('');
      const allTabIndex = allAvailableViews.findIndex((tab) => tab.name === 'All' && !tab.customView);
      handleTabChange(allTabIndex !== -1 ? allTabIndex : 0, 'All', 'main');
    }
  }, [selectedCustomView, customViewNames, allAvailableViews, handleTabChange]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleNewTabModalOpen = () => {
    setCreateOpen(true);
  };

  const handleCustomViewMenuChange = useCallback(
    (name: string) => {
      const index = allAvailableViews.findIndex((tab) => tab.name === name && tab.customView);
      if (index !== -1) {
        handleTabChange(index, name, 'custom');
      }
    },
    [allAvailableViews, handleTabChange]
  );

  const internalLinks = [
    {
      display: 'New Payment',
      onHandleClick: () => {
        setNewPaymentOpen(true);
      }
    },
    {
      url: '/payment/recipients',
      display: 'Payment Recipients'
    }
  ];

  return (
    <PageLayout
      dataCy="payment-page"
      dataTestId="payment-page"
      pageTitle="Payments"
      className="w-full mt-0"
      showInternalLinks={true}
      internalLinks={internalLinks}>
      <Fragment>
        <Tabs
          sx={DEFAULT_TAB_SX_STYLES}
          value={selectedTab}
          variant="scrollable"
          scrollButtons="auto"
          onChange={(_e, index) => {
            const currentTab = allAvailableViews[index];
            if (currentTab) {
              if (currentTab.customView) {
                handleTabChange(index, currentTab.name, 'custom');
              } else {
                handleTabChange(index, currentTab.name, 'main');
              }
            }
          }}
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: theme.palette.primary.main,
                height: allAvailableViews[selectedTab]?.customView ? 0 : 2,
                borderRadius: 1
              }
            }
          }}>
          {defaultTabs.map((tab, _index) => (
            <Tab
              sx={{ textTransform: 'none', fontSize: '.8rem', color: 'grey6' }}
              key={tab.name}
              label={
                <Tooltip
                  title={<span style={{ whiteSpace: 'pre-line' }}>{tab.tooltip}</span>}
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 'none',
                        width: 'auto'
                      }
                    }
                  }}>
                  <span>{tab.name}</span>
                </Tooltip>
              }
            />
          ))}
        </Tabs>
        <div className="-mt-12 float-right inline-flex pr-4">
          <div className={`flex ${selectedCustomView ? 'border-b-2 border-indigo' : ''}`}>
            <div
              key="custom-views"
              className="self-center">
              <CustomViewMenu
                setSelectedView={setSelectedCustomView}
                selectedView={selectedCustomView}
                viewOptions={customViewNames}
                handleViewChange={handleCustomViewMenuChange}
                defaultTitle="My Views"
              />
            </div>
          </div>
          <div className="ml-3 self-center">
            <Button
              onClick={() => setTriggerSave(true)}
              startIcon={<AddCircleIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Save As View
            </Button>
          </div>
          <div
            className="ml-3 self-center"
            key="add-custom-view">
            <Button
              onClick={() => setOpen(true)}
              startIcon={<EditNoteIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Edit Views
            </Button>
          </div>
        </div>
        {allAvailableViews.map((tab, index) => {
          let tabKeyName = tab.name || '';
          if (!tab.customView) {
            tabKeyName = tabKeyName.replace(/\s/g, '');
          }
          const tabKey = `paymentGrid_${tabKeyName}` as PrefKey;
          const colsForTab = getColsForTab(DefaultPaymentGridColumnDefs(), tab.disabledFilters ?? []);
          const gridSettingsForTab = userCustomizations?.content?.[tabKey];
          let initialState;
          if (gridSettingsForTab) {
            initialState = getInitialState(gridSettingsForTab, colsForTab, tabKey);
            const initialFilterModel = getInitialFilter(userCustomizations);
            if (initialState && initialFilterModel) {
              if (initialState.filter) {
                initialState.filter.filterModel = initialFilterModel;
              } else {
                initialState.filter = { filterModel: initialFilterModel };
              }
            }
          }

          return (
            <Fragment key={tab.name}>
              {(!initialState || paymentsLoading) && (
                <div className="flex h-[45rem] m-auto">
                  <ComponentSpinner label="Loading data..." />
                </div>
              )}
              {initialState && !paymentsLoading && userCustomizations && (
                <TabPanel
                  key={tab.name}
                  index={index}
                  value={selectedTab}
                  className="max-w-[60vw] min-w-full">
                  <UpstackDataGrid
                    title="Payments"
                    rows={payments?.data || []}
                    columns={colsForTab}
                    initialState={initialState}
                    page={tabKey}
                    gridSettings={gridSettingsForTab}
                    showSearch={false}
                    apiFilter={true}
                    fetchFunction={handleFetchPayments}
                    rowCount={payments?.totalCount}
                    advancedFiltering={true}
                    filterKeys={filterKeys}
                    triggerSave={triggerSave}
                    handleSaveComplete={() => setTriggerSave(false)}
                    handleNewTabModalOpen={handleNewTabModalOpen}
                  />
                </TabPanel>
              )}
            </Fragment>
          );
        })}
      </Fragment>
      <ViewsModal
        open={open}
        handleClose={handleClose}
        type="payment"
      />
      <NewViewModal
        onViewCreated={handleNewViewCreated}
        open={createOpen}
        setOpen={setCreateOpen}
        sourceTab={allAvailableViews[selectedTab]?.name || 'All'}
        sourceTabFilter={allAvailableViews[selectedTab]?.filter}
        type="payment"
      />
      <NewPaymentDialog
        open={newPaymentOpen}
        onClose={() => setNewPaymentOpen(false)}
        initialAsset={null}
      />
    </PageLayout>
  );
};
